import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, clearAuth, getUser } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import {
  Calendar, Car, Users, ShieldCheck, CreditCard, ScrollText, BarChart3,
  LogOut, Wrench, Settings as SettingsIcon, Zap, Map, Inbox, Banknote,
} from "lucide-react";
import BookingsTab from "@/components/admin/BookingsTab";
import DriversTab from "@/components/admin/DriversTab";
import DriverApplicationsTab from "@/components/admin/DriverApplicationsTab";
import SurgeTab from "@/components/admin/SurgeTab";
import ServicesTab from "@/components/admin/ServicesTab";
import StaffTab from "@/components/admin/StaffTab";
import PaymentMethodsTab from "@/components/admin/PaymentMethodsTab";
import AuditLogsTab from "@/components/admin/AuditLogsTab";
import ReportsTab from "@/components/admin/ReportsTab";
import PromosTab from "@/components/admin/PromosTab";
import BusinessTab from "@/components/admin/BusinessTab";
import SettingsTab from "@/components/admin/SettingsTab";
import FleetHeatmap from "@/components/admin/FleetHeatmap";
import ConsentLedgerTab from "@/components/admin/ConsentLedgerTab";
import ContactInboxTab from "@/components/admin/ContactInboxTab";
import CashOutTab from "@/components/admin/CashOutTab";

export default function AdminDashboard() {
  const nav = useNavigate();
  const user = getUser();
  const [inboxUnread, setInboxUnread] = useState(0);

  const isSuper = user?.role === "super_admin";

  // Poll unread contact-message count every 30s so admins see new tickets land
  // with a red dot on the sidebar without needing to open the inbox.
  useEffect(() => {
    if (!isSuper) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/inbox-unread-count");
        if (!cancelled) setInboxUnread(data?.unread || 0);
      } catch { /* silent — polling */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isSuper]);

  if (!user) return <Navigate to="/admin/login" replace />;
  const perms = user.permissions || [];
  const can = (p) => isSuper || perms.includes(p);

  const logout = () => { clearAuth(); toast.success("Signed out"); nav("/admin/login"); };

  const links = [
    can("bookings.read") && { to: "/admin", label: "Bookings", icon: Calendar, tid: TIDS.admin.sidebarBookings, end: true },
    can("drivers.read") && { to: "/admin/fleet", label: "Fleet map", icon: Map, tid: "sidebar-fleet-map" },
    can("reports.read") && { to: "/admin/reports", label: "Reports", icon: BarChart3, tid: TIDS.admin.sidebarReports },
    can("reports.read") && { to: "/admin/cash-out", label: "Cash-Out", icon: Banknote, tid: "sidebar-cash-out" },
    can("drivers.read") && { to: "/admin/drivers", label: "Drivers", icon: Car, tid: TIDS.admin.sidebarDrivers },
    can("applications.review") && { to: "/admin/applications", label: "Applications", icon: Users, tid: "sidebar-applications" },
    can("services.write") && { to: "/admin/services", label: "Services", icon: Wrench, tid: TIDS.admin.sidebarServices },
    isSuper && { to: "/admin/surge", label: "Surge", icon: Zap, tid: "sidebar-surge" },
    isSuper && { to: "/admin/staff", label: "Staff", icon: Users, tid: TIDS.admin.sidebarStaff },
    can("payments.write") && { to: "/admin/payments", label: "Payments", icon: CreditCard, tid: TIDS.admin.sidebarPayments },
    can("promos.write") && { to: "/admin/promos", label: "Promo Codes", icon: CreditCard, tid: "sidebar-promos" },
    can("business.write") && { to: "/admin/business", label: "Business", icon: CreditCard, tid: "sidebar-business" },
    can("audit.read") && { to: "/admin/audit", label: "Audit Logs", icon: ScrollText, tid: TIDS.admin.sidebarAudit },
    isSuper && { to: "/admin/consent-ledger", label: "Consent Ledger", icon: ShieldCheck, tid: "sidebar-consent-ledger" },
    isSuper && { to: "/admin/contact-inbox", label: "Contact Inbox", icon: Inbox, tid: "sidebar-contact-inbox", badge: inboxUnread },
    isSuper && { to: "/admin/settings", label: "Settings", icon: SettingsIcon, tid: "sidebar-settings" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#090D14] text-white flex">
      <aside className="w-64 shrink-0 border-r border-white/5 bg-[#0b1220] p-5 sticky top-0 h-screen flex flex-col">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-[#090D14] font-black text-lg font-display">T</div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-white">Triptap</div>
            <div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Dispatch</div>
          </div>
        </div>

        <nav className="space-y-1 flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-amber-400/10 text-amber-300 border border-amber-400/20" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <l.icon className="w-4 h-4" />
              <span className="flex-1">{l.label}</span>
              {l.badge > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold font-mono animate-pulse"
                  data-testid={`sidebar-badge-${l.to.split('/').pop()}`}
                >
                  {l.badge > 99 ? "99+" : l.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/5 pt-4 mt-4">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-amber-400/20 grid place-items-center text-amber-400 font-bold font-display">{user.name[0]}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-amber-400/80">
                {isSuper ? "Super Admin" : "Staff"}
              </div>
            </div>
          </div>
          <div className="mt-2" />
          <button data-testid={TIDS.admin.logout} onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 mt-2 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-6 sm:p-8 overflow-y-auto">
        <Routes>
          <Route index element={<BookingsTab isSuper={isSuper} />} />
          <Route path="fleet" element={<FleetHeatmap />} />
          <Route path="consent-ledger" element={<ConsentLedgerTab />} />
          <Route path="contact-inbox" element={<ContactInboxTab />} />
          <Route path="reports" element={<ReportsTab />} />
          <Route path="cash-out" element={<CashOutTab />} />
          <Route path="drivers" element={<DriversTab isSuper={isSuper} />} />
          <Route path="applications" element={<DriverApplicationsTab isSuper={isSuper} />} />
          <Route path="services" element={<ServicesTab />} />
          <Route path="surge" element={<SurgeTab />} />
          <Route path="staff" element={<StaffTab currentUserId={user.id} />} />
          <Route path="payments" element={<PaymentMethodsTab />} />
          <Route path="promos" element={<PromosTab />} />
          <Route path="business" element={<BusinessTab />} />
          <Route path="audit" element={<AuditLogsTab />} />
          <Route path="settings" element={<SettingsTab />} />
        </Routes>
      </main>
    </div>
  );
}
